import { LegalLayout, LegalSection } from "../component/legal/LegalLayout";
import { siteConfig } from "../config/site";

export function PrivacyPolicyPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      description={`How ${siteConfig.companyName} collects, uses, and protects your information.`}
    >
      <LegalSection>
        <p>
          This privacy policy is published in accordance with the Provisions
          of Rule 4(1) of the Information Technology (Reasonable Security
          Practices and Procedures and Sensitive Personal Data or
          Information) Rules 2011, which requires the publishing of a
          privacy policy for handling of or dealing in personal information
          including sensitive personal data or information.
        </p>
        <p>
          This website is owned and operated by {siteConfig.companyName}{" "}
          (referred to as "Company"){" "}
          {siteConfig.address
            ? `, having its principal place of business at ${siteConfig.address}, `
            : ", "}
          and is committed to protecting and respecting the privacy of its
          customers. Any person who enters or uses this website or shares
          information on this website through our mobile application is
          considered a customer for the purpose of this policy. If you do
          not agree to this policy or any part thereof, please do not
          use/access/download or install the platform or any part thereof.
        </p>
        <p>
          At {siteConfig.companyName}, we highly value the trust you place
          in us. We are committed to upholding ethical standards in the
          collection, usage, and protection of your information. This
          Privacy Policy governs the use of our website, applications,
          products, and services. By accessing and using our website or
          services, you indicate your acceptance of this policy and our
          terms of use.
        </p>
        <p>
          By accepting this Privacy Policy, you expressly consent to{" "}
          {siteConfig.companyName}'s collection, retention, analysis, use,
          and disclosure of your personal information.
        </p>
      </LegalSection>

      <LegalSection heading="Information We Collect">
        <p>At {siteConfig.companyName}, we collect the following types of information:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="font-semibold text-[#1c1917]">
              Personal Information:
            </span>{" "}
            This includes your name, contact information, and date of birth
            that you provide to us during the loan application process.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Financial Information:
            </span>{" "}
            We may collect financial details such as your bank account or
            card numbers to facilitate loan processing and repayment.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Transaction Information:
            </span>{" "}
            We gather data related to your loan applications, repayment
            history, and account balances to evaluate loan eligibility and
            manage your account.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Third-Party Information:
            </span>{" "}
            With your permission, we may access personal information stored
            by third parties, as permitted by your privacy settings and
            specific authorizations.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="How We Use Your Information">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="font-semibold text-[#1c1917]">
              Loan Processing:
            </span>{" "}
            We process and evaluate loan applications based on the
            information provided, ensuring efficient and effective loan
            disbursal.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Customer Support:
            </span>{" "}
            Our team is committed to providing dedicated customer support
            and helps resolve any disputes that may arise during your
            journey.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Legal and Regulatory Compliance:
            </span>{" "}
            We comply with applicable laws and regulations in handling your
            information to ensure a secure and transparent lending process.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Non-Personal Information:
            </span>{" "}
            We may use non-personal information to track website usage and
            customer behaviour, enabling us to enhance our services.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="Information Sharing">
        <p>
          By submitting your information, you authorize {siteConfig.companyName}{" "}
          to contact you via phone, SMS, RCS, WhatsApp, or email for
          informational and promotional purposes related to our services.
          You can opt out of promotional communication at any time by
          contacting us.
        </p>
        <p>We may share your information in the following circumstances:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="font-semibold text-[#1c1917]">
              Service Providers & Legal Requirements:
            </span>{" "}
            We may disclose your information when required by law, such as
            in response to a subpoena or court order, to comply with legal
            obligations and protect our rights.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Business Transfers:
            </span>{" "}
            In the event of a sale, merger, or transfer of our business,
            your information may be shared with the acquiring or merged
            entity to continue providing you with our services.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="Data Retention & Your Rights">
        <p>
          We retain your personal information for as long as it is
          necessary to provide you with our services or as required by law,
          ensuring your data is handled responsibly and securely. You have
          the right to request access to your personal information, seek
          corrections or deletions, and withdraw your consent for the
          processing of your data.
        </p>
      </LegalSection>

      <LegalSection heading="Cookies & Security">
        <p>
          We use cookies to enhance your website experience and understand
          how users interact with our services. We employ reasonable
          measures to safeguard your information against any unauthorized
          access, use, or disclosure.
        </p>
      </LegalSection>

      <LegalSection heading="Dispute Resolution">
        <p>
          Any disputes or claims arising from this Privacy Policy shall be
          subject to the exclusive jurisdiction of the courts having
          jurisdiction over the Company's registered office.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to This Privacy Policy">
        <p>
          We may periodically update this Privacy Policy. Continued use of
          our website, application, products, or services after such
          updates constitutes your acceptance of the revised policy.
        </p>
      </LegalSection>

      <LegalSection heading="Grievance Officer">
        <p>We have appointed a Grievance Officer to address any concerns. Please feel free to reach out:</p>
        <div className="flex flex-col gap-1 rounded-xl bg-[#f8fafc] p-5">
          {siteConfig.grievance.officerName ? (
            <p className="font-semibold text-[#1c1917]">
              {siteConfig.grievance.officerName}
            </p>
          ) : null}
          {siteConfig.phoneDisplay ? <p>{siteConfig.phoneDisplay}</p> : null}
          {siteConfig.grievance.officerEmail || siteConfig.email ? (
            <p>{siteConfig.grievance.officerEmail || siteConfig.email}</p>
          ) : null}
          {siteConfig.address ? <p>{siteConfig.address}</p> : null}
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
